import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import MyChildren from '../components/MyChildren';
import './CreateStory.css';

const HAIR_COLORS_EN = ['Blonde', 'Brown', 'Black', 'Red', 'Auburn', 'Light brown', 'Dark brown', 'Strawberry blonde', 'Gray', 'White'];
const HAIR_COLORS_DE = ['Blond', 'Braun', 'Schwarz', 'Rot', 'Rotbraun', 'Hellbraun', 'Dunkelbraun', 'Erdbeerblond', 'Grau', 'Weiß'];
const EYE_COLORS_EN = ['Blue', 'Green', 'Brown', 'Hazel', 'Gray', 'Amber', 'Dark brown', 'Light blue', 'Dark green'];
const EYE_COLORS_DE = ['Blau', 'Grün', 'Braun', 'Haselnussbraun', 'Grau', 'Bernsteinfarben', 'Dunkelbraun', 'Hellblau', 'Dunkelgrün'];

const STORY_THEMES_EN = [
  'A magical adventure in an enchanted forest',
  'Discovering a secret treasure map',
  'Making a new friend at the playground',
  'Learning to ride a bike',
  'A day at the beach with family',
  'Visiting grandparents on the farm',
  'The first day at a new school',
  'A birthday party surprise',
  'Helping animals in the neighborhood',
  'A camping trip under the stars',
  'Custom theme (enter your own)'
];

const STORY_THEMES_DE = [
  'Ein magisches Abenteuer im Zauberwald',
  'Eine geheime Schatzkarte entdecken',
  'Einen neuen Freund auf dem Spielplatz finden',
  'Fahrradfahren lernen',
  'Ein Tag am Strand mit der Familie',
  'Großeltern auf dem Bauernhof besuchen',
  'Der erste Tag in der neuen Schule',
  'Eine Geburtstagsparty-Überraschung',
  'Tieren in der Nachbarschaft helfen',
  'Ein Campingausflug unter den Sternen',
  'Eine Weihnachtsgeschichte',
  'Eigenes Thema (selbst eingeben)'
];

export default function CreateStory({ user, refreshUser }) {
  const navigate = useNavigate();
  // Multi-child support: array of selected children (profiles or one-time)
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [showChildrenPopup, setShowChildrenPopup] = useState(false);
  const [showOneTimeModal, setShowOneTimeModal] = useState(false);
  const [oneTimeFormData, setOneTimeFormData] = useState({
    name: '',
    age: 5,
    description: '',
    relationship: 'main',
    hair_color: 'brown',
    eye_color: 'brown'
  });
  const [formData, setFormData] = useState({
    // Theme fields
    language: user?.language || 'en',
    theme: '',
    // Interactive story option
    is_interactive: false,
  });
  const [customTheme, setCustomTheme] = useState('');
  const [childProfiles, setChildProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weeklyLimit, setWeeklyLimit] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Relationship options for multi-child stories
  const RELATIONSHIPS = {
    main: { en: 'Main child', de: 'Hauptkind', icon: '●' },
    sister: { en: 'Sibling', de: 'Geschwisterkind', icon: '◐' },
    brother: { en: 'Sibling', de: 'Geschwisterkind', icon: '◐' },
    friend: { en: 'Friend', de: 'Freund/in', icon: '◆' },
    cousin: { en: 'Cousin', de: 'Cousin/Cousine', icon: '◇' }
  };
  const RELATIONSHIP_OPTIONS = ['main', 'sister', 'friend', 'cousin'];
  
  const MAX_CHILDREN = 3;

  const isGerman = formData.language === 'de';
  const t = (key) => getTranslation(formData.language, key);

  useEffect(() => {
    fetchWeeklyLimit();
    fetchChildProfiles();
  }, []);

  useEffect(() => {
    const savedDraft = localStorage.getItem('landingStoryDraft');
    if (!savedDraft) {
      return;
    }

    try {
      const parsedDraft = JSON.parse(savedDraft);
      if (!parsedDraft?.child_name) {
        return;
      }

      setFormData((current) => ({
        ...current,
        language: parsedDraft.language || current.language,
        theme: parsedDraft.theme_label || current.theme
      }));

      setSelectedChildren((currentChildren) => {
        if (currentChildren.length > 0) {
          return currentChildren;
        }

        return [{
          name: parsedDraft.child_name,
          age: parsedDraft.age || 5,
          relationship: 'main',
          hair_color: 'brown',
          eye_color: 'brown',
          isOneTime: true,
          isDraft: true
        }];
      });

      toast.success(
        (parsedDraft.language || formData.language) === 'de'
          ? `Entwurf fuer ${parsedDraft.child_name} wurde uebernommen`
          : `Draft for ${parsedDraft.child_name} was loaded`
      );
      localStorage.removeItem('landingStoryDraft');
    } catch (error) {
      console.error('Failed to load landing story draft', error);
    }
  }, []);

  const fetchChildProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/child-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Failed to fetch child profiles');
    }
  };

  const fetchWeeklyLimit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/weekly-limit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Weekly limit response:', response.data);
      setWeeklyLimit(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly limit:', error);
    }
  };

  const handleChildSelect = (profile) => {
    // Toggle selection - add or remove from selectedChildren
    const isAlreadySelected = selectedChildren.some(c => c.profile_id === profile.id);
    
    if (isAlreadySelected) {
      // Remove from selection
      setSelectedChildren(selectedChildren.filter(c => c.profile_id !== profile.id));
    } else {
      // Add to selection (max 3 children)
      if (selectedChildren.length >= MAX_CHILDREN) {
        toast.error(isGerman ? `Maximal ${MAX_CHILDREN} Kinder pro Geschichte` : `Maximum ${MAX_CHILDREN} children per story`);
        return;
      }
      setSelectedChildren([...selectedChildren, {
        profile_id: profile.id,
        name: profile.name,
        age: profile.age,
        relationship: profile.relationship || 'friend',
        photo_url: profile.photo_url,
        isProfile: true
      }]);
    }
  };

  const handleRemoveChild = (childToRemove) => {
    if (childToRemove.profile_id) {
      setSelectedChildren(selectedChildren.filter(c => c.profile_id !== childToRemove.profile_id));
    } else {
      // One-time child - remove by name
      setSelectedChildren(selectedChildren.filter(c => c.name !== childToRemove.name || c.isProfile));
    }
  };

  const handleSaveOneTimeChild = () => {
    if (!oneTimeFormData.name) {
      toast.error(isGerman ? 'Bitte einen Namen eingeben' : 'Please enter a name');
      return;
    }
    
    // Check max children limit
    if (selectedChildren.length >= MAX_CHILDREN) {
      toast.error(isGerman ? `Maximal ${MAX_CHILDREN} Kinder pro Geschichte` : `Maximum ${MAX_CHILDREN} children per story`);
      return;
    }
    
    // Add one-time child to selection
    const newChild = {
      name: oneTimeFormData.name,
      age: oneTimeFormData.age,
      relationship: oneTimeFormData.relationship,
      description: oneTimeFormData.description,
      hair_color: oneTimeFormData.hair_color,
      eye_color: oneTimeFormData.eye_color,
      isOneTime: true
    };
    
    setSelectedChildren([...selectedChildren, newChild]);
    setShowOneTimeModal(false);
    
    // Reset form
    setOneTimeFormData({
      name: '',
      age: 5,
      description: '',
      relationship: 'main',
      hair_color: 'brown',
      eye_color: 'brown'
    });
    
    toast.success(isGerman ? 'Kind hinzugefügt!' : 'Child added!');
  };

  const handleClearAllChildren = () => {
    setSelectedChildren([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: At least one child must be selected
    if (selectedChildren.length === 0) {
      toast.error(isGerman ? 'Bitte wähle mindestens ein Kind aus' : 'Please select at least one child');
      return;
    }

    // Check weekly limit
    console.log('Current weekly limit state:', weeklyLimit);
    if (weeklyLimit && !weeklyLimit.can_create) {
      setShowUpgradeModal(true);
      return;
    }

    const theme = formData.theme === 'Custom theme (enter your own)' || formData.theme === 'Eigenes Thema (selbst eingeben)' 
      ? customTheme 
      : formData.theme;

    if (!theme) {
      toast.error(isGerman ? 'Bitte wähle ein Thema' : 'Please select a theme');
      return;
    }

    setLoading(true);

    const token = localStorage.getItem('token');
    const formDataToSend = new FormData();

    // Build children JSON for multi-child support
    const childrenForApi = selectedChildren.map(child => {
      if (child.isProfile || child.profile_id) {
        return { profile_id: child.profile_id };
      } else {
        return {
          name: child.name,
          age: child.age,
          relationship: child.relationship || 'friend',
          hair_color: child.hair_color || 'brown',
          eye_color: child.eye_color || 'brown'
        };
      }
    });

    // Send as JSON
    formDataToSend.append('children_json', JSON.stringify(childrenForApi));
    
    // For backward compatibility, also send first child data
    const firstChild = selectedChildren[0];
    if (firstChild.isProfile || firstChild.profile_id) {
      formDataToSend.append('character_mode', 'photo');
      formDataToSend.append('child_profile_id', firstChild.profile_id);
    } else {
      formDataToSend.append('character_mode', 'properties');
      formDataToSend.append('child_name', firstChild.name);
      formDataToSend.append('age', parseInt(firstChild.age));
      if (firstChild.description) {
        formDataToSend.append('child_description', firstChild.description);
      }
    }

    formDataToSend.append('theme', theme);
    formDataToSend.append('language', formData.language);
    formDataToSend.append('is_interactive', formData.is_interactive);
    
    // Debug logging
    console.log('Creating story with:', {
      children: childrenForApi,
      theme,
      language: formData.language,
      is_interactive: formData.is_interactive,
      isMultiChild: selectedChildren.length > 1
    });

    try {
      const response = await axios.post(`${API}/stories/create`, formDataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const placeholderStory = response.data;
      console.log('Story placeholder created:', placeholderStory);
      
      const childNames = selectedChildren.map(c => c.name).join(', ');
      toast.success(isGerman 
        ? `Geschichte für ${childNames} wird erstellt! Du wirst zum Dashboard weitergeleitet...` 
        : `Story for ${childNames} is being created! Redirecting to dashboard...`);
      
      navigate('/dashboard', {
        replace: true,
        state: { generatingStory: placeholderStory }
      });
      
    } catch (error) {
      console.error('Story creation error:', error);
      toast.error(error.response?.data?.detail || t('toasts.failedToCreateStory'));
      setLoading(false);
    }
  };

  const CURRENT_THEMES = formData.language === 'de' ? STORY_THEMES_DE : STORY_THEMES_EN;
  const HAIR_COLORS = formData.language === 'de' ? HAIR_COLORS_DE : HAIR_COLORS_EN;
  const EYE_COLORS = formData.language === 'de' ? EYE_COLORS_DE : EYE_COLORS_EN;

  return (
    <div className="create-story-page">
      <div className="create-header">
        <Button 
          data-testid="back-to-dashboard-button"
          variant="outline" 
          onClick={() => navigate('/dashboard')}
          disabled={loading}
        >
          ← {isGerman ? 'Zurück' : 'Back'}
        </Button>
      </div>

      <div className="create-container">
        <div className="create-logo-section">
          <img 
            src={`${window.location.origin}/storytales-logo.png`}
            alt="StoryTales Logo" 
            className="logo-image"
            onError={(e) => {
              e.target.src = `${window.location.origin}/storytales-logo.png`;
            }}
          />
          <p className="tagline">Erstelle magische personalisierte Geschichten für Kinder</p>
        </div>

        <div className="create-hero">
          <div className="hero-icon">✨</div>
          <h1 data-testid="create-story-title">
            {isGerman ? 'Erstelle eine magische Geschichte' : 'Create a Magical Story'}
          </h1>
          <p>{isGerman ? 'Personalisiert für dein Kind' : 'Personalized just for your child'}</p>
        </div>

        {weeklyLimit && (
          <div className="limit-info">
            <p>
              {isGerman ? 'Verwendete Geschichten' : 'Stories used'}: {weeklyLimit.used} / {weeklyLimit.limit}
              {weeklyLimit.purchased_unused > 0 && ` + ${weeklyLimit.purchased_unused} ${isGerman ? 'gekauft' : 'purchased'}`}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="story-form">
          {/* Box 1: Select Children (Multi-Child Support) */}
          <div className="form-section">
            <h2 className="section-title">
              {isGerman ? 'Für wen ist die Geschichte?' : 'Who is the story for?'}
              <span className="info-icon" title={isGerman ? 'Wähle bis zu 3 Kinder für eine gemeinsame Geschichte (Geschwister, Freunde).' : 'Select up to 3 children for a story together (siblings, friends).'}>
                ℹ️
              </span>
            </h2>
            
            {/* Selected Children Display */}
            {selectedChildren.length > 0 && (
              <div className="selected-children-bar">
                <span className="selected-label">
                  {isGerman ? 'Ausgewählt' : 'Selected'} ({selectedChildren.length}/{MAX_CHILDREN}):
                </span>
                <div className="selected-children-list">
                  {selectedChildren.map((child, index) => (
                    <div key={child.profile_id || `onetime-${index}`} className="selected-child-chip">
                      <span className="chip-icon">{RELATIONSHIPS[child.relationship]?.icon || '👤'}</span>
                      <span className="chip-name">{child.name}</span>
                      <span className="chip-relationship">
                        ({isGerman ? RELATIONSHIPS[child.relationship]?.de : RELATIONSHIPS[child.relationship]?.en})
                      </span>
                      <button 
                        type="button" 
                        className="chip-remove"
                        onClick={() => handleRemoveChild(child)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                {selectedChildren.length > 1 && (
                  <button 
                    type="button" 
                    className="clear-all-btn"
                    onClick={handleClearAllChildren}
                  >
                    {isGerman ? 'Alle entfernen' : 'Clear all'}
                  </button>
                )}
              </div>
            )}
            
            <div className="children-selector">
              {childProfiles.map((profile) => {
                const isSelected = selectedChildren.some(c => c.profile_id === profile.id);
                return (
                  <div
                    key={profile.id}
                    className={`child-option ${isSelected ? 'selected' : ''} ${selectedChildren.length >= MAX_CHILDREN && !isSelected ? 'disabled' : ''}`}
                    onClick={() => handleChildSelect(profile)}
                  >
                    <div className="child-photo-circle">
                      {profile.photo_url ? (
                        <img 
                          src={profile.photo_url} 
                          alt={profile.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const initial = document.createElement('div');
                            initial.className = 'profile-initial';
                            initial.textContent = profile.name.charAt(0).toUpperCase();
                            e.target.parentElement.appendChild(initial);
                            e.target.parentElement.classList.add('profile-no-photo');
                          }}
                        />
                      ) : (
                        <div className="profile-initial">{profile.name.charAt(0).toUpperCase()}</div>
                      )}
                      {isSelected && <div className="selected-checkmark">✓</div>}
                    </div>
                    <div className="child-info-inline">
                      <span className="child-name">{profile.name}</span>
                      <span className="child-age">{profile.age}</span>
                      {profile.relationship && profile.relationship !== 'friend' && (
                        <span className="child-relationship">{RELATIONSHIPS[profile.relationship]?.icon}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <Button
                type="button"
                variant="outline"
                className="new-child-btn"
                onClick={() => setShowChildrenPopup(true)}
                disabled={selectedChildren.length >= MAX_CHILDREN}
              >
                + {isGerman ? 'Neues Kind' : 'New Child'}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="new-child-btn one-time-btn"
                onClick={() => {
                  setOneTimeFormData({ 
                    name: '', 
                    age: 5, 
                    description: '',
                    relationship: 'main',
                    hair_color: 'brown',
                    eye_color: 'brown'
                  });
                  setShowOneTimeModal(true);
                }}
                disabled={selectedChildren.length >= MAX_CHILDREN}
              >
                + {isGerman ? 'Einmaliges Kind' : 'One-Time Child'}
              </Button>
            </div>
            
            {selectedChildren.length > 1 && (
              <div className="multi-child-info">
                <span className="multi-child-badge">👨‍👩‍👧‍👦</span>
                <span>
                  {isGerman 
                    ? 'Diese Geschichte wird für alle ausgewählten Kinder zusammen erstellt!' 
                    : 'This story will be created for all selected children together!'}
                </span>
              </div>
            )}
          </div>

          {/* Box 2: Theme */}
          <div className="form-section">
            <h2 className="section-title">
              {isGerman ? 'Thema' : 'Theme'}
              <span className="info-icon" title={isGerman ? 'Wähle die Sprache der Geschichte und ein Thema aus der Liste aus. Du möchtest eine ganz persönliche Geschichte kreiieren? Dann wähle eigenes Thema und beschreibe im Feld Eigenes Thema eingeben, welches Abenteuer dein Kind erleben soll.' : 'Choose the language and a theme from the list. Want to create a completely personal story? Then select custom theme and describe in the Enter Custom Theme field what adventure your child should experience.'}>
                ℹ️
              </span>
            </h2>
            
            <div className="form-group">
              <Label>{isGerman ? 'Sprache' : 'Language'}</Label>
              <Select
                value={formData.language}
                onValueChange={(value) => setFormData({ ...formData, language: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>{isGerman ? 'Geschichte Thema' : 'Story Theme'}</Label>
              <Select
                value={formData.theme}
                onValueChange={(value) => setFormData({ ...formData, theme: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isGerman ? 'Wähle ein Thema...' : 'Select a theme...'} />
                </SelectTrigger>
                <SelectContent>
                  {CURRENT_THEMES.map((theme, idx) => (
                    <SelectItem key={idx} value={theme}>{theme}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(formData.theme === 'Custom theme (enter your own)' || formData.theme === 'Eigenes Thema (selbst eingeben)') && (
              <div className="form-group">
                <Label>{isGerman ? 'Eigenes Thema eingeben' : 'Enter Custom Theme'}</Label>
                <Input
                  type="text"
                  value={customTheme}
                  onChange={(e) => setCustomTheme(e.target.value)}
                  placeholder={isGerman ? 'Beschreibe dein eigenes Thema...' : 'Describe your own theme...'}
                />
              </div>
            )}

            {/* Interactive Story Feature */}
            <div className="form-group interactive-story-feature" style={{ 
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', 
              padding: '20px', 
              borderRadius: '12px', 
              margin: '20px 0',
              color: 'white',
              boxShadow: '0 4px 15px rgba(20, 184, 166, 0.24)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: 'white', 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    ✨ {isGerman ? 'Interaktive Geschichte' : 'Interactive Story'}
                    <span style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'normal'
                    }}>
                      {isGerman ? 'NEU' : 'NEW'}
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: '14px', 
                    margin: '8px 0 0 0', 
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: '1.4'
                  }}>
                    {isGerman 
                      ? 'Teste unsere neue Funktion für interaktive Geschichten! Lass dein Kind entscheiden wie die Geschichte endet!' 
                      : 'Try our new interactive story feature! Let your child decide how the story ends!'
                    }
                  </p>
                </div>
                
                {/* Custom Toggle Switch - Mobile Friendly */}
                <div 
                  onClick={() => setFormData({ ...formData, is_interactive: !formData.is_interactive })}
                  style={{
                    position: 'relative',
                    width: '56px',
                    height: '32px',
                    background: formData.is_interactive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.3)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid rgba(255,255,255,0.5)',
                    flexShrink: 0
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: formData.is_interactive ? '24px' : '2px',
                    width: '24px',
                    height: '24px',
                    background: formData.is_interactive ? '#0d9488' : 'white',
                    borderRadius: '50%',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </div>
            </div>
          </div>

          <Button 
            type={weeklyLimit && !weeklyLimit.can_create ? "button" : "submit"}
            disabled={loading} 
            className="submit-button"
            style={weeklyLimit && !weeklyLimit.can_create ? {
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              color: 'white',
              cursor: 'pointer'
            } : {}}
            onClick={weeklyLimit && !weeklyLimit.can_create ? () => setShowUpgradeModal(true) : undefined}
          >
            {weeklyLimit && !weeklyLimit.can_create 
              ? (isGerman ? '💎 Upgrade für mehr Geschichten' : '💎 Upgrade for More Stories')
              : loading 
                ? (isGerman ? 'Erstelle Geschichte...' : 'Creating Story...') 
                : (isGerman ? '✨ Geschichte erstellen' : '✨ Create Story')
            }
          </Button>

          {/* Limit reached info message */}
          {weeklyLimit && !weeklyLimit.can_create && (
            <div style={{
              textAlign: 'center',
              marginTop: '15px',
              padding: '15px',
              background: 'linear-gradient(135deg, #f0fdfa 0%, #f7fffd 100%)',
              border: '2px dashed #14b8a6',
              borderRadius: '12px',
              color: '#14b8a6'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
                📚 {isGerman ? 'Geschichtenlimit erreicht!' : 'Story limit reached!'}
              </p>
              <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                {isGerman 
                  ? 'Klicken Sie den Button oben um weitere Geschichten zu erhalten'
                  : 'Click the button above to get more stories'}
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Children Management Popup */}
      {showChildrenPopup && (
        <div className="popup-overlay" onClick={() => setShowChildrenPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowChildrenPopup(false)}>×</button>
            <MyChildren 
              user={user} 
              language={formData.language}
              onProfilesChange={(updatedProfiles) => {
                setChildProfiles(updatedProfiles);
              }}
              onProfileCreated={(newProfile) => {
                // Auto-add the newly created child profile to selection
                if (selectedChildren.length < MAX_CHILDREN) {
                  setSelectedChildren([...selectedChildren, {
                    profile_id: newProfile.id,
                    name: newProfile.name,
                    age: newProfile.age,
                    relationship: newProfile.relationship || 'friend',
                    photo_url: newProfile.photo_url,
                    isProfile: true
                  }]);
                  toast.success(isGerman ? 'Kind hinzugefügt!' : 'Child added!');
                }
              }}
            />
            <Button onClick={() => setShowChildrenPopup(false)}>
              {isGerman ? 'Fertig' : 'Done'}
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', textAlign: 'center' }}>
            <div style={{ marginBottom: '20px' }}>
              <span style={{ fontSize: '48px', marginBottom: '10px', display: 'block' }}>📚</span>
              <h2 style={{ color: '#14b8a6', marginBottom: '10px' }}>
                {isGerman ? 'Geschichtenlimit erreicht!' : 'Story Limit Reached!'}
              </h2>
              <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.5' }}>
                {isGerman 
                  ? 'Sie haben alle verfügbaren Geschichten aufgebraucht. Wählen Sie eine Option, um weiterzumachen:' 
                  : 'You\'ve used all your available stories. Choose an option to continue:'}
              </p>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '15px',
              marginBottom: '20px' 
            }}>
              {/* Single Story Option */}
              <div style={{
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                ':hover': { borderColor: '#14b8a6' }
              }} onClick={() => {
                navigate('/pricing');
                // Scroll to single story section
                setTimeout(() => {
                  const element = document.getElementById('single-story-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{ margin: '0 0 5px 0', color: '#333', fontSize: '18px' }}>
                      💳 {isGerman ? 'Eine Geschichte kaufen' : 'Buy Single Story'}
                    </h3>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                      {isGerman ? 'Nur €2.99 - Sofort verfügbar' : 'Just €2.99 - Available immediately'}
                    </p>
                  </div>
                  <div style={{ fontSize: '18px', color: '#14b8a6' }}>→</div>
                </div>
              </div>
              
              {/* Pro Subscription Option */}
              <div style={{
                border: '2px solid #14b8a6',
                borderRadius: '12px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(20,184,166,0.1) 0%, rgba(13,148,136,0.1) 100%)',
                cursor: 'pointer',
                position: 'relative'
              }} onClick={() => {
                navigate('/pricing');
                // Scroll to pro section
                setTimeout(() => {
                  const element = document.getElementById('pro-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '15px',
                  background: '#14b8a6',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {isGerman ? 'BELIEBT' : 'POPULAR'}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h3 style={{ margin: '0 0 5px 0', color: '#14b8a6', fontSize: '18px' }}>
                    ⭐ {isGerman ? 'Pro Upgrade' : 'Pro Upgrade'}
                  </h3>
                  <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                    {isGerman 
                      ? '4 Geschichten/Monat + Premium Features' 
                      : '4 stories/month + Premium features'}
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => setShowUpgradeModal(false)}
              style={{ width: '100%' }}
            >
              {isGerman ? 'Später entscheiden' : 'Decide Later'}
            </Button>
          </div>
        </div>
      )}

      {/* One-Time Story Modal */}
      {showOneTimeModal && (
        <div className="popup-overlay" onClick={() => setShowOneTimeModal(false)}>
          <div className="popup-content one-time-modal" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowOneTimeModal(false)}>×</button>
            
            <h3 className="modal-title">
              ✨ {isGerman ? 'Einmaliges Kind hinzufügen' : 'Add One-Time Child'}
            </h3>
            <p className="modal-subtitle">
              {isGerman 
                ? 'Füge ein Kind für diese Geschichte hinzu, ohne ein Profil zu speichern' 
                : 'Add a child for this story without saving a profile'}
            </p>
            
            <div className="form-row">
              <div className="form-group">
                <label>{isGerman ? 'Name' : 'Name'} *</label>
                <input
                  type="text"
                  value={oneTimeFormData.name}
                  onChange={(e) => setOneTimeFormData({ ...oneTimeFormData, name: e.target.value })}
                  placeholder={isGerman ? 'z.B. Max' : 'e.g. Emma'}
                  required
                />
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Alter' : 'Age'} *</label>
                <select
                  value={oneTimeFormData.age}
                  onChange={(e) => setOneTimeFormData({ ...oneTimeFormData, age: parseInt(e.target.value) })}
                  required
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(age => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>{isGerman ? 'Rolle in der Geschichte' : 'Role in the story'} *</label>
              <div className="relationship-options">
                {RELATIONSHIP_OPTIONS.map((key) => {
                  const value = RELATIONSHIPS[key];
                  return (
                  <button
                    key={key}
                    type="button"
                    className={`relationship-btn ${oneTimeFormData.relationship === key ? 'selected' : ''}`}
                    onClick={() => setOneTimeFormData({ ...oneTimeFormData, relationship: key })}
                  >
                    <span className="rel-icon">{value.icon}</span>
                    <span className="rel-label">{isGerman ? value.de : value.en}</span>
                  </button>
                  );
                })}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{isGerman ? 'Haarfarbe' : 'Hair Color'}</label>
                <select
                  value={oneTimeFormData.hair_color}
                  onChange={(e) => setOneTimeFormData({ ...oneTimeFormData, hair_color: e.target.value })}
                >
                  {(isGerman ? HAIR_COLORS_DE : HAIR_COLORS_EN).map(color => (
                    <option key={color} value={color.toLowerCase()}>{color}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Augenfarbe' : 'Eye Color'}</label>
                <select
                  value={oneTimeFormData.eye_color}
                  onChange={(e) => setOneTimeFormData({ ...oneTimeFormData, eye_color: e.target.value })}
                >
                  {(isGerman ? EYE_COLORS_DE : EYE_COLORS_EN).map(color => (
                    <option key={color} value={color.toLowerCase()}>{color}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>{isGerman ? 'Beschreibung (optional)' : 'Description (optional)'}</label>
              <textarea
                rows="3"
                value={oneTimeFormData.description}
                onChange={(e) => setOneTimeFormData({ ...oneTimeFormData, description: e.target.value })}
                placeholder={isGerman 
                  ? 'Weitere Details zum Kind: Kleidungsstil, besondere Merkmale...' 
                  : 'Additional details: clothing style, distinctive features...'}
              />
            </div>

            <div className="modal-actions">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowOneTimeModal(false)}
              >
                {isGerman ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button 
                type="button"
                onClick={handleSaveOneTimeChild}
              >
                {isGerman ? 'Hinzufügen' : 'Add Child'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
